import { useRef } from 'react';
import { FileUp, Sparkles, ShieldCheck, Zap, MousePointerClick } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useViewer } from '@/features/viewer/ViewerContext';
import { useViewerStore } from '@/state/viewerStore';

export function EmptyState() {
  const inputRef = useRef<HTMLInputElement>(null);
  const { loadFile, loadSample } = useViewer();
  const modelName = useViewerStore((s) => s.modelName);
  const ready = useViewerStore((s) => s.ready);

  if (modelName !== null) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center px-4">
      <div
        className="pointer-events-auto w-full max-w-xl rounded-2xl border border-panel-border bg-panel/85 p-8 text-center shadow-2xl backdrop-blur-md"
        style={{ boxShadow: '0 25px 80px -20px rgba(47, 129, 247, 0.25)' }}
      >
        <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-accent/15 text-accent">
          <FileUp className="h-7 w-7" />
        </div>

        <h2 className="mb-2 text-2xl font-semibold tracking-tight">Open your first IFC model</h2>
        <p className="mx-auto mb-6 max-w-sm text-sm text-muted">
          Drop a <code className="rounded bg-white/5 px-1.5 py-0.5 text-xs">.ifc</code> file
          anywhere on this page, or pick one below. Files stay on your device.
        </p>

        <input
          ref={inputRef}
          type="file"
          accept=".ifc"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void loadFile(file);
            e.target.value = '';
          }}
        />

        <div className="mb-6 flex flex-wrap items-center justify-center gap-2">
          <Button
            size="lg"
            onClick={() => inputRef.current?.click()}
            disabled={!ready}
          >
            <FileUp className="h-4 w-4" />
            Open from your computer
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => void loadSample()}
            disabled={!ready}
          >
            <Sparkles className="h-4 w-4" />
            Try the sample model
          </Button>
        </div>

        {!ready && (
          <p className="mb-4 text-xs text-muted">Initializing IFC engine…</p>
        )}

        <div className="grid grid-cols-1 gap-3 border-t border-panel-border pt-5 text-left sm:grid-cols-3">
          <Tip
            icon={<ShieldCheck className="h-4 w-4" />}
            title="Private"
            text="Files never leave your browser."
          />
          <Tip
            icon={<Zap className="h-4 w-4" />}
            title="Fast"
            text="Native-speed WebAssembly parsing."
          />
          <Tip
            icon={<MousePointerClick className="h-4 w-4" />}
            title="Inspect"
            text="Click any element for properties."
          />
        </div>
      </div>
    </div>
  );
}

function Tip({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="flex gap-2.5">
      <div className="mt-0.5 text-accent">{icon}</div>
      <div>
        <div className="text-xs font-semibold text-foreground">{title}</div>
        <div className="text-xs text-muted">{text}</div>
      </div>
    </div>
  );
}
